import { useDispatch, useSelector } from 'react-redux';
import { useToggleAttendanceMutation, useGetTodayStatusQuery } from '../features/attendance/attendanceApi';
import {
    selectIsClockedIn,
    selectTodaySummary,
    selectFormattedTotalHours,
    selectSlots,
    selectActiveSlot,
    selectNextSlot,
    selectAllShiftsDone,
    selectIsWeekOff,
    selectIsHalfDay,
    selectEssClockEnabled
} from '../features/attendance/attendanceSlice';
import { toast } from 'react-hot-toast';
import { getCurrentPosition } from '../utils/geolocation';

/**
 * Custom hook to share attendance logic across components (Header, Dashboard, etc.)
 */
export const useAttendance = () => {
    const dispatch = useDispatch();
    const isClockedIn = useSelector(selectIsClockedIn);
    const todaySummary = useSelector(selectTodaySummary);
    const formattedTotalHours = useSelector(selectFormattedTotalHours);
    const slots = useSelector(selectSlots);
    const activeSlot = useSelector(selectActiveSlot);
    const nextSlot = useSelector(selectNextSlot);
    const allShiftsDone = useSelector(selectAllShiftsDone);
    const isWeekOff = useSelector(selectIsWeekOff);
    const isHalfDay = useSelector(selectIsHalfDay);
    const essClockEnabled = useSelector(selectEssClockEnabled);

    const [toggle, { isLoading: isToggling }] = useToggleAttendanceMutation();
    // refetchOnMountOrArgChange (a per-call option, not an endpoint-level
    // one — RTK Query only allows it at createApi() or the hook call site):
    // this hook is shared across the persistent header (mounts once per
    // session) and any dedicated Attendance page (mounts fresh on every
    // navigation to it). refetchOnFocus (see app/store.ts) covers the
    // header's case; this covers the page's — opening the clock-in/out
    // screen always checks current status instead of trusting whatever was
    // cached from earlier in the session.
    const { isFetching, refetch } = useGetTodayStatusQuery(undefined, { refetchOnMountOrArgChange: true });

    const handleToggle = async () => {
        if (allShiftsDone) {
            toast.error("All of today's scheduled shifts have already been completed.");
            return;
        }
        try {
            // Best-effort — if the branch has no geofencing configured the backend
            // ignores these fields entirely, so it's safe to always attempt this.
            const { position } = await getCurrentPosition();

            const res = await toggle({
                deviceInfo: navigator.userAgent,
                sessionId: crypto.randomUUID(),
                lat: position?.lat,
                lng: position?.lng,
                accuracy: position?.accuracy,
            }).unwrap();

            if (res.success) {
                const newStatus = !!(res.data.first_in && !res.data.last_out);
                const shiftLabel = res.data.slot === 2 ? ' (Shift 2)' : '';
                toast.success((newStatus ? 'Shift Started' : 'Shift Ended') + shiftLabel, {
                    icon: newStatus ? '🌅' : '🌙',
                });
            }
        } catch (err: any) {
            const apiError = err.data?.error;
            const message = typeof apiError === 'string' ? apiError : apiError?.message;
            toast.error(message || 'Failed to update shift status');
        }
    };

    return {
        isClockedIn,
        todaySummary,
        formattedTotalHours,
        slots,
        activeSlot,
        nextSlot,
        allShiftsDone,
        isWeekOff,
        isHalfDay,
        essClockEnabled,
        handleToggle,
        isLoading: isToggling || isFetching,
        refresh: refetch
    };
};
